/**
 * DAC profiles -- group 08 (professional / mastering converters and the
 * chip-free outliers).
 *
 * The unifying theme of this group is that the interesting engineering is
 * usually NOT the converter chip: it is the clock recovery, the I/V and line
 * driver, and the reconstruction filter. Each entry therefore states the facts
 * that change the DSP -- architecture, core, output stage, published dynamic
 * range and THD+N, output voltage and impedance, filter set -- and lets the kit
 * expand them. Figures recalled from published specifications are tagged
 * MANUFACTURER_SPEC; anything inferred from topology is ESTIMATED, and the
 * provenance note on every entry says which is which. Nothing here is a
 * measurement.
 */

import type { DacProfile } from '../../types/hardware';
import {
  ESS_FILTERS,
  FILTERS,
  HARMONIC_SHAPES,
  SIMPLE_FILTERS,
  makeDac,
  usd,
} from '../kit';

export const DACS_08: DacProfile[] = [
  makeDac({
    mfr: 'Prism Sound',
    model: 'Callia',
    price: usd(2600, 'APPROXIMATE', 2020, 'Approximate US retail. Prism Sound sells through pro dealers and the figure is recalled to the nearest hundred rather than transcribed from a price list.'),
    year: 2016,
    country: 'United Kingdom',
    architecture: 'delta-sigma',
    core: 'Delta-sigma converter behind the Prism CleverClox two-stage hybrid PLL clock recovery',
    outputStage: 'op-amp',
    dynamicRangeDb: 115,
    thdPlusNDb: -105,
    crosstalkDb: -110,
    outputVrmsSE: 2.0,
    outputImpedanceOhms: 50,
    balanced: false,
    maxBitDepth: 24,
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000, 352800, 384000],
    dsd: 'dsd128',
    filters: SIMPLE_FILTERS(),
    defaultFilterId: 'sharp-linear',
    jitterPsRms: 5,
    noiseShaperOrder: 5,
    nonlinear: { amount: 0.24, asymmetry: 0.06, headroomDb: 15 },
    confidence: 'ESTIMATED',
    accent: '#5bc0eb',
    tags: ['professional', 'mastering', 'cleverclox', 'headphone-out'],
    notes:
      'The dominant engineering signature of this unit is clock recovery, not conversion. CleverClox is a two-stage hybrid PLL whose second stage has an extremely long time constant, so incoming interface jitter is attenuated to a residual well below the converter noise floor and the modelled jitter sidebands effectively disappear regardless of source quality. Everything after that is deliberately unremarkable: an IC line driver at 2 Vrms into 50 ohms, a single fixed sharp linear-phase reconstruction filter, and no balanced output at all, so this profile behaves like a low-impedance voltage source with symmetric pre and post ringing.',
    provenanceNote:
      'Architecture, CleverClox clocking and the unbalanced 2 Vrms output are from Prism Sound published material. Dynamic range, THD+N and crosstalk are ESTIMATED at values typical of Prism converters rather than transcribed from the datasheet, and the single fixed filter is a modelling choice: Prism does not document a user-selectable reconstruction filter on Callia.',
  }),

  makeDac({
    mfr: 'Lavry Engineering',
    model: 'DA11',
    price: usd(1975, 'APPROXIMATE', 2015, 'Approximate US retail while current. Long-running mastering product with little public price movement; figure recalled, not transcribed.'),
    year: 2007,
    country: 'United States',
    architecture: 'multibit-delta-sigma',
    core: 'Multi-bit sigma-delta modulator with Lavry CrystalLock two-stage clock recovery',
    outputStage: 'op-amp',
    dynamicRangeDb: 117,
    thdPlusNDb: -104,
    crosstalkDb: -104,
    outputVrmsSE: 2.0,
    outputVrmsBal: 4.0,
    outputImpedanceOhms: 30,
    balanced: true,
    maxBitDepth: 24,
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000],
    dsd: 'none',
    filters: [FILTERS.sharpLinear(96)],
    effectiveBits: 19,
    jitterPsRms: 10,
    noiseShaperOrder: 4,
    nonlinear: { amount: 0.28, asymmetry: 0.1, headroomDb: 13 },
    confidence: 'ESTIMATED',
    accent: '#9fb3c8',
    tags: ['professional', 'mastering', 'crystallock', 'analog-attenuator'],
    notes:
      'A multi-bit modulator rather than a single-bit one, which matters here because the quantiser has more than two states: idle tones and pattern noise are far weaker than in a 1-bit design, so the modelled noise floor is smooth and stationary rather than lightly tonal. Attenuation is done in the analog domain after conversion, so level changes do not cost resolution the way a digital volume control does. The ceiling is 24/192 with no DSD path at all, and the single fixed 96-tap linear-phase filter is short by modern standards, which trades a little stopband depth for markedly shorter pre-ringing than the long-tap designs in this group.',
    provenanceNote:
      'Architecture, the analog output attenuator, CrystalLock clock recovery and the 24-bit/192 kHz ceiling are from Lavry Engineering published material. Dynamic range, THD+N, crosstalk and output impedance are ESTIMATED from class norms; the filter length is a modelling choice, as Lavry does not publish tap counts.',
  }),

  makeDac({
    mfr: 'Crane Song',
    model: 'Solaris',
    price: usd(3300, 'APPROXIMATE', 2020, 'Approximate US retail; sold direct and through pro dealers.'),
    year: 2011,
    country: 'United States',
    architecture: 'hybrid',
    core: 'Crane Song Quantum D/A conversion feeding a discrete high-bias analog stage; converter core not detailed publicly',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 120,
    thdPlusNDb: -100,
    crosstalkDb: -98,
    outputVrmsSE: 2.0,
    outputVrmsBal: 4.0,
    outputImpedanceOhms: 60,
    balanced: true,
    maxBitDepth: 24,
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000],
    dsd: 'none',
    filters: [FILTERS.apodizing(128), FILTERS.sharpLinear(128)],
    defaultFilterId: 'apodizing',
    jitterPsRms: 12,
    slewRateVus: 20,
    nonlinear: { kind: 'jfet-square', amount: 0.42, asymmetry: 0.4, bias: 0.5, headroomDb: 13 },
    confidence: 'ESTIMATED',
    accent: '#d08770',
    tags: ['professional', 'mastering', 'discrete-analog', 'monitor-converter'],
    notes:
      'What separates this from the other two pro converters here is the analog stage: a high-bias discrete Class-A line driver instead of an integrated one. Its residue is second-harmonic dominant and grows smoothly with level rather than sitting at the fixed, high-order floor an IC output produces, which is why this entry gets a JFET-square transfer curve while the Prism and Lavry entries get sigma-delta shaping. The modelled apodizing default trades roughly 20 dB of stopband depth for the removal of pre-ringing on band-edge transients.',
    provenanceNote:
      'The discrete Class-A analog output and the Quantum D/A designation are from Crane Song published material; the converter core itself is not publicly detailed, which is why architecture is recorded as hybrid rather than a specific family. Dynamic range, THD+N, output impedance and the filter behaviour are ESTIMATED or modelled. Crane Song does not publish a full THD+N figure for Solaris.',
  }),

  makeDac({
    mfr: 'Bel Canto Design',
    model: 'DAC3.7',
    price: usd(3495, 'MSRP', 2013),
    year: 2013,
    country: 'United States',
    architecture: 'delta-sigma',
    core: '24-bit/192 kHz delta-sigma converter locked to the Bel Canto Ultra-Clock master oscillator',
    outputStage: 'op-amp',
    dynamicRangeDb: 121,
    thdPlusNDb: -108,
    crosstalkDb: -115,
    outputVrmsSE: 2.0,
    outputVrmsBal: 4.0,
    outputImpedanceOhms: 100,
    balanced: true,
    maxBitDepth: 24,
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000],
    dsd: 'none',
    filters: SIMPLE_FILTERS(),
    defaultFilterId: 'sharp-linear',
    jitterPsRms: 3,
    noiseShaperOrder: 5,
    idleToneLevel: 0.08,
    nonlinear: { amount: 0.3, asymmetry: 0.07, headroomDb: 14 },
    confidence: 'ESTIMATED',
    accent: '#7fb069',
    tags: ['ultra-clock', 'preamp-capable', 'digital-volume'],
    notes:
      'Designed around the master clock rather than around an exotic converter: the oscillator is the low-phase-noise part and the converter is fed synchronously from it, so the modelled jitter is the lowest in this group. The output is intended to drive a power amplifier directly through a volume control implemented ahead of the converter, which is the real trade-off to be aware of -- attenuating in the digital domain shifts the signal down toward the quantiser floor, so the effective dynamic range at low listening levels is lower than the headline figure. The 100 ohm source impedance is high enough that a long interconnect into a low-impedance input starts to matter.',
    provenanceNote:
      'Price, the Ultra-Clock master oscillator, direct-drive volume operation and the 24/192 ceiling are from Bel Canto published material. Dynamic range, THD+N, crosstalk and jitter are ESTIMATED; Bel Canto does not publish a converter part number, and the op-amp output-stage classification is inferred from the product description rather than from a schematic.',
  }),

  makeDac({
    mfr: 'Wyred 4 Sound',
    model: 'DAC-2v2SE',
    price: usd(4499, 'APPROXIMATE', 2023, 'Approximate US retail. Wyred 4 Sound sells direct and revises pricing and options frequently, so treat this as a bracket rather than a quoted MSRP.'),
    year: 2019,
    country: 'United States',
    architecture: 'delta-sigma',
    core: 'ESS ES9038PRO, all eight internal converter channels paralleled per side',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 129,
    thdPlusNDb: -115,
    crosstalkDb: -130,
    outputVrmsSE: 2.1,
    outputVrmsBal: 4.2,
    outputImpedanceOhms: 100,
    balanced: true,
    maxBitDepth: 32,
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000, 352800, 384000],
    dsd: 'dsd512',
    filters: ESS_FILTERS(),
    defaultFilterId: 'sharp-linear',
    parallelUnits: 8,
    jitterPsRms: 15,
    noiseShaperOrder: 6,
    idleToneLevel: 0.04,
    nonlinear: { kind: 'jfet-square', amount: 0.34, asymmetry: 0.3, bias: 0.45 },
    confidence: 'ESTIMATED',
    accent: '#8fb8ff',
    tags: ['ess-sabre', 'paralleled-channels', 'user-filters'],
    notes:
      'Eight internal converter channels summed per output: uncorrelated quantisation noise averages down by about 9 dB relative to a single channel, which is where the very high dynamic range figure comes from, while correlated distortion products do not average down at all. That asymmetry is the point of the design and it is why this entry pairs a state-of-the-art noise floor with a distortion figure only about 15 dB better than the discrete pro converters above it. The full six-option Hyperstream filter set is exposed, so pre-ringing here is a user variable rather than a fixed property.',
    provenanceNote:
      'Converter part, channel paralleling, DSD512 support and the selectable filter set are from Wyred 4 Sound and ESS published material. Dynamic range and THD+N are ESTIMATED near the ES9038PRO datasheet ceiling rather than transcribed from a Wyred 4 Sound measurement; output impedance and the discrete Class-A output-stage classification are inferred from the product description.',
  }),

  makeDac({
    mfr: 'Exogal',
    model: 'Comet',
    price: usd(2500, 'APPROXIMATE', 2016, 'Approximate US retail for the base Comet; the version with the upgraded external supply listed higher. Company no longer trading, so this is a historic figure.'),
    year: 2014,
    country: 'United States',
    architecture: 'fpga-custom',
    core: 'Proprietary FPGA conversion engine; no off-the-shelf converter chip in the signal path',
    outputStage: 'passive-iv',
    dynamicRangeDb: 118,
    thdPlusNDb: -102,
    crosstalkDb: -100,
    outputVrmsSE: 2.0,
    outputVrmsBal: 4.0,
    outputImpedanceOhms: 300,
    balanced: true,
    maxBitDepth: 24,
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000],
    dsd: 'none',
    filters: [FILTERS.hybrid(96)],
    internalOversampling: 16,
    jitterPsRms: 20,
    nonlinear: { kind: 'tanh', amount: 0.3, asymmetry: 0.18, headroomDb: 12 },
    confidence: 'ESTIMATED',
    accent: '#b48ead',
    tags: ['fpga', 'chip-free', 'no-active-iv', 'discontinued-brand'],
    notes:
      'Conversion and reconstruction both happen inside the FPGA, so the filter is not one of a vendor chip preset family: it is modelled here as a hybrid-phase response, part way between linear and minimum phase, with the pre-ringing split unevenly across the transient. The second consequence that actually changes system behaviour is the 300 ohm source impedance -- roughly six times the pro converters here -- which forms a divider with the input impedance and cable capacitance of whatever follows, so this profile is the one in the group that audibly changes depending on what it is plugged into.',
    provenanceNote:
      'The FPGA-based, chip-free conversion claim is from Exogal published material. Dynamic range, THD+N and jitter are ESTIMATED; Exogal published almost no conventional measurements. The passive I/V output-stage classification and the 300 ohm source impedance are MODELLING CHOICES inferred from the description of a chip-free converter, not documented circuit facts, and should be read as such.',
  }),

  makeDac({
    mfr: 'Resonessence Labs',
    model: 'Invicta Mirus',
    price: usd(4999, 'APPROXIMATE', 2016, 'Approximate US retail while current; brand no longer in production.'),
    year: 2014,
    country: 'Canada',
    architecture: 'delta-sigma',
    core: 'ESS ES9018S driven by Resonessence-authored FPGA interpolation ahead of the chip filter',
    outputStage: 'jfet',
    dynamicRangeDb: 124,
    thdPlusNDb: -110,
    crosstalkDb: -120,
    outputVrmsSE: 2.5,
    outputVrmsBal: 5.0,
    outputImpedanceOhms: 75,
    balanced: true,
    maxBitDepth: 32,
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000, 352800, 384000],
    dsd: 'dsd128',
    filters: [FILTERS.apodizing(128), FILTERS.sharpLinear(128), FILTERS.slowMinimum(32), FILTERS.hybrid(96)],
    defaultFilterId: 'apodizing',
    jitterPsRms: 6,
    noiseShaperOrder: 6,
    internalOversampling: 8,
    nonlinear: { kind: 'jfet-square', amount: 0.3, asymmetry: 0.28, bias: 0.4, headroomDb: 14 },
    confidence: 'ESTIMATED',
    accent: '#a3be8c',
    tags: ['ess-sabre', 'fpga-filters', 'apodizing', 'user-loadable-filters'],
    notes:
      'The distinguishing feature is that the interpolation is done in an FPGA in front of the converter, so the filter is authored rather than selected from a chipset menu, and the default is an apodizing design intended to suppress pre-ringing from the recording chain as well as its own. Compared with the other Sabre-based entry here, this one runs a single stereo pair rather than eight paralleled channels, which costs about 5 dB of dynamic range but removes any channel-summing mismatch, and it drives 5 Vrms balanced, high enough to overload some preamp inputs.',
    provenanceNote:
      'Converter part, the FPGA-based user-loadable filter architecture and the apodizing default are from Resonessence Labs published material. Dynamic range, THD+N, output voltage and impedance are ESTIMATED from ES9018S-class norms; the JFET output-stage classification is inferred, as Resonessence did not publish an output-stage schematic.',
  }),

  makeDac({
    mfr: 'Mark Levinson',
    model: 'No. 30.6 Reference DAC',
    price: usd(16500, 'APPROXIMATE', 1999, 'Approximate original US retail in the late 1990s, recalled rather than transcribed. Long discontinued; used values are far below this.'),
    year: 1999,
    country: 'United States',
    architecture: 'ladder-multibit',
    core: 'Dual-differential multi-bit ladder conversion, one converter pair per channel, 8x oversampling digital filter',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 108,
    thdPlusNDb: -96,
    crosstalkDb: -100,
    outputVrmsSE: 2.2,
    outputVrmsBal: 4.4,
    outputImpedanceOhms: 6,
    balanced: true,
    maxBitDepth: 20,
    sampleRates: [32000, 44100, 48000, 88200, 96000],
    dsd: 'none',
    filters: [FILTERS.sharpLinear(128)],
    ladderMismatchLsb: 0.25,
    effectiveBits: 18,
    jitterPsRms: 25,
    harmonicShape: HARMONIC_SHAPES.r2r,
    confidence: 'ESTIMATED',
    accent: '#c0c5ce',
    tags: ['ladder', 'dual-differential', 'vintage-reference', 'discontinued'],
    notes:
      'A ladder converter behaves nothing like a modulator at low signal level, and that is the whole reason this entry exists in the group. Ladder element mismatch produces integral nonlinearity that scales with the code being output, so its distortion does not collapse into the noise as level drops the way a delta-sigma design does -- modelled here as an r2r-INL transfer curve at 0.25 LSB, low because the converter pairs were trimmed and run dual-differential, which cancels even-order INL error and leaves a mainly odd-order residue. Note that this is an oversampling ladder with a linear-phase filter, not a non-oversampling design, so no zero-order-hold droop is applied and pre-ringing is present.',
    provenanceNote:
      'The dual-differential multi-bit ladder topology, the discrete Class-A output and the low output impedance are from Mark Levinson published material for the No. 30 series; the exact converter module type fitted to the 30.6 revision is NOT confirmed here and is deliberately not named. Dynamic range, THD+N, ladder mismatch and jitter are ESTIMATED from late-1990s 20-bit ladder norms rather than transcribed from a datasheet.',
  }),
];
